/* PJC v13 — Couche « Noël » : neige (canvas), traîneau du Père Noël, ornements flottants de l'accueil.
   Chargé en différé par snippets/pjc-global-head.liquid, seulement en saison Noël avec au moins une animation activée.
   Lit html[data-pjc-noel-snow|sleigh|garland]. Respecte prefers-reduced-motion (aucune neige ni traîneau).
   Performance : un seul canvas, sprites pré-dessinés, ~30 images/s sur mobile, pause quand l'onglet est caché. */
(() => {
  const html = document.documentElement;
  const on = (k) => html.getAttribute('data-pjc-noel-' + k) === 'on';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia('(max-width: 749px)');

  /* ------------------------------------------------------------------ neige */
  function snow() {
    if (!on('snow') || reduced.matches) return;
    const canvas = document.createElement('canvas');
    canvas.className = 'pjc-snow';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let W = 0, H = 0, dpr = 1, flakes = [], sprites = [], raf = 0, last = 0, running = false;

    const makeSprite = (r) => {
      const s = document.createElement('canvas');
      const pad = 3;
      s.width = s.height = Math.ceil((r + pad) * 2 * dpr);
      const c = s.getContext('2d');
      c.scale(dpr, dpr);
      const cx = r + pad;
      // halo doré très léger pour rester visible sur fond crème
      const g = c.createRadialGradient(cx, cx, 0, cx, cx, r + 2);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.55, 'rgba(255,255,255,0.95)');
      g.addColorStop(0.8, 'rgba(236,216,173,0.35)');
      g.addColorStop(1, 'rgba(212,170,98,0)');
      c.fillStyle = g;
      c.beginPath();
      c.arc(cx, cx, r + 2, 0, Math.PI * 2);
      c.fill();
      return { img: s, size: (r + pad) * 2 };
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sprites = [makeSprite(1.6), makeSprite(2.4), makeSprite(3.4)];
      const target = Math.max(22, Math.min(70, Math.round((W * H) / (mobile.matches ? 26000 : 20000))));
      while (flakes.length < target) flakes.push(spawn(true));
      flakes.length = target;
    };

    const spawn = (anywhere) => ({
      x: Math.random() * W,
      y: anywhere ? Math.random() * H : -8,
      s: Math.floor(Math.random() * 3),
      vy: 14 + Math.random() * 26, // px / s
      drift: 6 + Math.random() * 14,
      ph: Math.random() * Math.PI * 2,
      o: 0.45 + Math.random() * 0.5,
    });

    const frame = (t) => {
      if (!running) return;
      raf = requestAnimationFrame(frame);
      const minDt = mobile.matches ? 33 : 16;
      if (t - last < minDt) return;
      const dt = Math.min(64, t - last || 16) / 1000;
      last = t;
      ctx.clearRect(0, 0, W, H);
      for (const f of flakes) {
        f.y += f.vy * dt;
        f.ph += dt * 0.9;
        f.x += Math.sin(f.ph) * f.drift * dt;
        if (f.y > H + 8 || f.x < -10 || f.x > W + 10) Object.assign(f, spawn(false));
        const sp = sprites[f.s];
        ctx.globalAlpha = f.o;
        ctx.drawImage(sp.img, f.x - sp.size / 2, f.y - sp.size / 2, sp.size, sp.size);
      }
      ctx.globalAlpha = 1;
    };

    const start = () => {
      if (running) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(frame);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    resize();
    window.addEventListener('resize', resize, { passive: true });
    document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));
    reduced.addEventListener('change', () => (reduced.matches ? (stop(), canvas.remove()) : start()));
    requestAnimationFrame(() => canvas.setAttribute('data-ready', ''));
    start();
  }

  /* --------------------------------------------------------------- traîneau */
  const SLEIGH =
    '<svg viewBox="0 0 220 70" aria-hidden="true" focusable="false">' +
    '<g fill="currentColor">' +
    // rennes (deux silhouettes)
    '<path d="M18 46c-3 1-4 5-2 7l3 1 2-5c2 1 5 2 8 2s7-1 9-3l2 5 3-1c2-2 1-6-2-7l1-6c3-1 6-3 7-6l3 1 1-3-4-2c-1-3-4-5-7-5h-6l-2-5-3 1 1 5c-3 1-5 3-6 6-4 1-8 4-8 8v7z"/>' +
    '<path d="M64 46c-3 1-4 5-2 7l3 1 2-5c2 1 5 2 8 2s7-1 9-3l2 5 3-1c2-2 1-6-2-7l1-6c3-1 6-3 7-6l3 1 1-3-4-2c-1-3-4-5-7-5h-6l-2-5-3 1 1 5c-3 1-5 3-6 6-4 1-8 4-8 8v7z"/>' +
    // bois
    '<path d="M33 18l-2-6 3-1 2 5zM41 17l1-6 3 1-2 6zM79 18l-2-6 3-1 2 5zM87 17l1-6 3 1-2 6z"/>' +
    // rênes
    '<path d="M48 30q30-8 68 10" stroke="currentColor" stroke-width="1.6" fill="none"/>' +
    '<path d="M94 30q14-4 24 8" stroke="currentColor" stroke-width="1.6" fill="none"/>' +
    // traîneau
    '<path d="M118 56c-6 0-10-4-10-9V36h58c6 0 12 4 14 9l2 5h-8c0-4-3-7-7-7h-45v7h22v6h-26z"/>' +
    '<path d="M110 62h66c4 0 8-2 10-6h-6c-1 2-3 3-4 3h-66z"/>' +
    // Père Noël
    '<path d="M150 36v-8c0-4 3-7 7-7s7 3 7 7v8z"/>' +
    '<circle cx="157" cy="16" r="5"/>' +
    '<path d="M151 14c1-5 5-8 9-7l4 5-2 1-3-3c-3 0-5 1-6 4z"/>' +
    // sac de cadeaux
    '<path d="M166 36c0-6 4-11 9-11s9 5 9 11z"/>' +
    '</g>' +
    // touches dorées : nez, pompon, étoile
    '<g fill="#d4aa62"><circle cx="8" cy="31" r="2.2"/><circle cx="54" cy="31" r="2.2"/><circle cx="164" cy="12" r="2"/>' +
    '<path d="M196 8l1.4 4.1 4.3.1-3.4 2.6 1.2 4.2-3.5-2.5-3.5 2.5 1.2-4.2-3.4-2.6 4.3-.1z"/></g>' +
    '</svg>';

  function sleigh() {
    if (!on('sleigh') || reduced.matches) return;
    const el = document.createElement('div');
    el.className = 'pjc-sleigh';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = SLEIGH;
    document.body.appendChild(el);
    let timer = 0;
    const run = () => {
      if (document.hidden || reduced.matches) return schedule(20000);
      el.classList.remove('pjc-sleigh--run');
      void el.offsetWidth; // redémarre l'animation
      el.classList.add('pjc-sleigh--run');
    };
    const schedule = (ms) => {
      clearTimeout(timer);
      timer = setTimeout(run, ms);
    };
    el.addEventListener('animationend', (e) => {
      if (e.animationName === 'pjc-sleigh-fly') {
        el.classList.remove('pjc-sleigh--run');
        schedule(75000 + Math.random() * 30000);
      }
    });
    schedule(2600);
  }

  /* ---------------------------------------------------- ornements de l'accueil */
  const ICONS = {
    sapin:
      '<svg viewBox="0 0 48 48"><path d="M24 4l9 12h-5l8 10h-5l9 11H8l9-11h-5l8-10h-5z" fill="#245c45"/><path d="M21 37h6v7h-6z" fill="#6b4a3a"/><path d="M24 2l1.6 3.4 3.7.5-2.7 2.6.7 3.7L24 10.4l-3.3 1.8.7-3.7-2.7-2.6 3.7-.5z" fill="#d4aa62"/><g fill="#b83732"><circle cx="20" cy="19" r="2"/><circle cx="29" cy="27" r="2"/><circle cx="18" cy="31" r="2"/></g><g fill="#ecd8ad"><circle cx="27" cy="14" r="1.5"/><circle cx="21" cy="26" r="1.5"/><circle cx="30" cy="34" r="1.5"/></g></svg>',
    cadeau:
      '<svg viewBox="0 0 48 48"><rect x="7" y="18" width="34" height="24" rx="4" fill="#b83732"/><rect x="5" y="14" width="38" height="9" rx="3" fill="#d1574f"/><path d="M21 14h6v28h-6z" fill="#ecd8ad"/><path d="M24 14c-6 0-9-3-9-6s3-4 5-3 4 5 4 9zm0 0c6 0 9-3 9-6s-3-4-5-3-4 5-4 9z" fill="#d4aa62"/></svg>',
    etoile:
      '<svg viewBox="0 0 48 48"><path d="M24 3l5.6 12.6 13.7 1.4-10.3 9.2 2.9 13.5L24 32.8l-11.9 6.9 2.9-13.5L4.7 17l13.7-1.4z" fill="#d4aa62"/><path d="M24 10l3.4 7.6 8.3.9-6.2 5.6 1.7 8.2L24 28.1l-7.2 4.2 1.7-8.2-6.2-5.6 8.3-.9z" fill="#ecd8ad"/></svg>',
    flocon:
      '<svg viewBox="0 0 48 48"><g stroke="#fff" stroke-width="3" stroke-linecap="round" fill="none"><path d="M24 5v38M7.5 14.5l33 19M40.5 14.5l-33 19M24 5l-4 4M24 5l4 4M24 43l-4-4M24 43l4-4M7.5 14.5l1.5 5.5M7.5 14.5l5.5-1.5M40.5 33.5l-1.5-5.5M40.5 33.5l-5.5 1.5M40.5 14.5l-1.5 5.5M40.5 14.5l-5.5-1.5M7.5 33.5l1.5-5.5M7.5 33.5l5.5 1.5"/></g><g stroke="#d4aa62" stroke-opacity=".6" stroke-width="1" stroke-linecap="round" fill="none"><path d="M24 5v38M7.5 14.5l33 19M40.5 14.5l-33 19"/></g></svg>',
  };

  function garland() {
    if (!on('garland')) return;
    const host = document.querySelector('.pjx-hero__composition');
    if (!host) return;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    const place = [
      ['etoile', '6%', '4%', 38, '7s', '0s', '6deg'],
      ['sapin', '-2%', '58%', 54, '8s', '-2s', '-4deg'],
      ['cadeau', '84%', '8%', 42, '6.5s', '-4s', '5deg'],
      ['flocon', '90%', '70%', 36, '9s', '-1s', '0deg'],
    ];
    for (const [k, x, y, w, t, d, r] of place) {
      const o = document.createElement('span');
      o.className = 'pjc-noel-orn';
      o.setAttribute('aria-hidden', 'true');
      o.style.cssText = `--x:${x};--y:${y};--w:${w}px;--t:${t};--d:${d};--r:${r}`;
      o.innerHTML = ICONS[k];
      host.appendChild(o);
    }
  }

  const boot = () => {
    garland();
    snow();
    sleigh();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
